/* eslint-disable max-len */
import { Fauxdown } from '../../../concepts/text/Fauxdown';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { TextMargin, TextSettings, textAlignmentToFlexAlign, textAlignmentToTextAlign } from '../entities/TextConventions';

const debug = false;
const depth = { value: 0 };
const fitSpan = document.createElement('div');
fitSpan.id = '__tr_span';
fitSpan.style.background = 'rgba(255,0,0,0.5)';
const fitContainer = document.createElement('div');
fitContainer.id = '__tr_container';
fitContainer.style.background = 'rgba(255,0,0,0.5)';
fitContainer.style.pointerEvents = 'none';
if (!debug) fitContainer.style.overflow = 'hidden';
if (debug) fitContainer.style.zIndex = '1000';
if (debug) fitContainer.style.left = '100px';
if (debug) fitContainer.style.top = '100px';
fitContainer.appendChild(fitSpan);

const padValue = (value: string) => !!value ? (value.endsWith(' ') ? value : value + ' ') : ' ';

const applyAlignment = (elmt: HTMLSpanElement, ts: TextSettings) => {
    elmt.style.display = 'flex';
    elmt.style.flexDirection = 'column';
    elmt.style.justifyContent = textAlignmentToFlexAlign(ts.textAlignV);
    elmt.style.alignItems = textAlignmentToFlexAlign(ts.textAlignH);
    elmt.style.textAlign = textAlignmentToTextAlign(ts.textAlignH);
};

const resetAlignment = (elmt: HTMLSpanElement) => {
    elmt.style.display = 'block';
    elmt.style.justifyContent = 'initial';
    elmt.style.alignItems = 'initial';
    elmt.style.textAlign = 'initial';
};

const applyStyling = (elmt: HTMLSpanElement, ts: TextSettings) => {
    elmt.style.padding = TextMargin.toPx(ts.textMargin);
    elmt.style.color = ts.fontColor;
    elmt.style.fontFamily = ts.fontFamily;
    elmt.style.fontSize = ts.fontSize + 'px';
    elmt.style.fontStyle = ts.fontStyle;
    elmt.style.fontWeight = ts.fontWeight.toString();
    elmt.style.lineHeight = (ts.fontSize * ts.lineHeight) + 'px';
};

const wrap = (str, maxWidth) => {
    let res = '';
    while (str.length > maxWidth) {
        let found = false;
        // Inserts new line at first whitespace of the line
        for (let i = maxWidth - 1; i >= 0; i--) {
            if (/^\s$/.test(str.charAt(i))) {
                res = res + [str.slice(0, i), '\n'].join('');
                str = str.slice(i + 1);
                found = true;
                break;
            }
        }
        // Inserts new line at maxWidth position, the word is too long to wrap
        if (!found) {
            res += [str.slice(0, maxWidth), '\n'].join('');
            str = str.slice(maxWidth);
        }
    }

    return res + str;
};

const prepareHtml = (value: string) => {
    value = padValue(value);
    return `<div class="ql-text">${Fauxdown.toEditorForm(value)}</div>`;
};

export const TextRuler = {

    measureSize(value: string, ts: TextSettings, textWidth?: number): Point {
        value = value?.toString();

        try {
            depth.value++;

            fitContainer.style.position = 'fixed';
            fitContainer.style.width = '100000px';
            fitContainer.style.height = '100000px';

            fitSpan.style.position = 'absolute';
            fitSpan.style.whiteSpace = 'pre-wrap';
            fitSpan.style.overflowWrap = 'anywhere';

            resetAlignment(fitContainer);
            applyStyling(fitSpan, ts);
            fitSpan.innerHTML = prepareHtml(!!textWidth ? wrap(value, textWidth) : value);

            document.body.appendChild(fitContainer);

            // Use this instead of clientWidth/Height because it is accurate to more than 0 dp.
            const rect = fitSpan.getBoundingClientRect();

            return new Point(
                Math.ceil(rect.width),
                Math.ceil(Math.max(rect.height, ts.fontSize)));
        }
        finally {
            depth.value--;
            if (depth.value === 0 && !debug) {
                document.body.removeChild(fitContainer);
            }
        }
    },

    measureHeight(value: string, ts: TextSettings, width: number): number {
        value = value?.toString();

        try {
            depth.value++;

            fitContainer.style.position = 'fixed';
            fitContainer.style.width = width + 'px';
            fitContainer.style.height = '0px';

            fitSpan.style.position = 'static';
            fitSpan.style.whiteSpace = 'pre-wrap';
            fitSpan.style.overflowWrap = 'anywhere';

            resetAlignment(fitContainer);
            applyStyling(fitSpan, ts);
            fitSpan.innerHTML = prepareHtml(value);

            document.body.appendChild(fitContainer);

            return Math.ceil(Math.max(fitSpan.clientHeight, ts.fontSize));
        }
        finally {
            depth.value--;
            if (depth.value === 0 && !debug) {
                document.body.removeChild(fitContainer);
            }
        }
    },

    measureLayout(value: string, ts: TextSettings, width: number, height: number): Rect[] {
        value = value?.toString();

        try {
            depth.value++;

            fitContainer.style.position = 'fixed';
            fitContainer.style.width = `${width}px`;
            fitContainer.style.height = `${height}px`;

            fitSpan.style.position = 'static';
            fitSpan.style.whiteSpace = 'pre-wrap';
            fitSpan.style.overflowWrap = 'anywhere';

            applyAlignment(fitContainer, ts);
            applyStyling(fitSpan, ts);

            fitSpan.innerHTML = `<span id="__tr_text">${prepareHtml(value)}</span>`;

            document.body.appendChild(fitContainer);

            const fitText = document.getElementById('__tr_text');
            return Array.from(fitText.getClientRects()).map(x => Rect.fromLike(x)).filter(x => x.area() > 0);
        }
        finally {
            depth.value--;
            if (depth.value === 0 && !debug) {
                document.body.removeChild(fitContainer);
            }
        }
    },

    fit(value: string, ts: TextSettings, width: number, height: number): number {
        const h = fitHorizontally(value, ts, width, height);
        const v = fitVertically(value, ts, width, height);
        return Math.min(h, v);
    },

};

const fitHorizontally = (value: string, ts: TextSettings, width: number, _height: number): number => {

    const longest = (value.split(/\s+/).sort((a, b) => b.length - a.length)[0] || '').substr(0, 16);
    const tts = TextSettings.from(ts);

    let size = ts.maxFontSize || 1024;
    let vector = size;

    for (let cb = 0; cb < 1000; cb++) {
        tts.fontSize = size;
        const delta = width - TextRuler.measureSize(longest, tts).x;
        if (delta > 0 && vector === 0.5) {
            return size;
        }
        vector = Math.ceil(vector) / 2;
        size = (delta > 0) ? size + vector : size - vector;
        if (size < 0) break;
    }

    return Math.max(0, size);
};

const fitVertically = (value: string, ts: TextSettings, width: number, height: number): number => {

    const tts = TextSettings.from(ts);

    let size = ts.maxFontSize || 1024;
    let vector = size;

    for (let cb = 0; cb < 1000; cb++) {
        tts.fontSize = size;
        const delta = height - TextRuler.measureHeight(value, tts, width);
        if (delta > 0 && vector === 0.5) {
            return size;
        }
        vector = Math.ceil(vector) / 2;
        size = (delta > 0) ? size + vector : size - vector;
        if (size < 0) break;
    }

    return Math.max(0, size);
};
